import type { MouseEvent } from 'react';

import { useColorModeValue } from '@ecoinvent/ui';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath } from '@xyflow/react';

import { COLORS, DIM_OPACITY, FUNCTIONAL_ACCENT_DARK, FUNCTIONAL_ACCENT_LIGHT } from './constants';

export type SupplyChainEdgeData = {
  strokeWidth?: number;
  edgeVariant?: 'input' | 'consumer';
  isFunctional?: boolean;
  mode?: 'flows' | 'impact_assessment';
  dimmed?: boolean;
  hovered?: boolean;
  onHover?: (id: string, hovered: boolean) => void;
  onDeselect?: (event: MouseEvent) => void;
};

const SupplyChainEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  label,
}: EdgeProps) => {
  const edgeData = data as SupplyChainEdgeData | undefined;
  const isDimmed = edgeData?.dimmed ?? false;
  const isHovered = edgeData?.hovered ?? false;
  const strokeWidth = edgeData?.strokeWidth ?? 2;
  const isConsumer = edgeData?.edgeVariant === 'consumer';
  const isFlowsMode = edgeData?.mode === 'flows';
  const isFunctional = (edgeData?.isFunctional ?? false) && edgeData?.mode === 'impact_assessment';

  const inputEdgeColor = useColorModeValue(COLORS.greenLight, COLORS.greenDark);
  const consumerEdgeColor = useColorModeValue(COLORS.purpleLight, COLORS.purpleDark);
  const flowsEdgeColor = useColorModeValue(COLORS.grayDim, COLORS.grayDark);
  const hoverInputEdgeColor = useColorModeValue(COLORS.greenLight, COLORS.greenDark);
  const hoverConsumerEdgeColor = useColorModeValue(COLORS.hoverConsumerLineLight, COLORS.hoverConsumerLineDark);
  const functionalEdgeColor = useColorModeValue(FUNCTIONAL_ACCENT_LIGHT, FUNCTIONAL_ACCENT_DARK);

  const normalColor = isFlowsMode
    ? flowsEdgeColor
    : isFunctional
      ? functionalEdgeColor
      : isConsumer
        ? consumerEdgeColor
        : inputEdgeColor;
  const hoverColor = isFunctional ? functionalEdgeColor : isConsumer ? hoverConsumerEdgeColor : hoverInputEdgeColor;
  const edgeColor = isHovered ? hoverColor : normalColor;

  const labelBg = useColorModeValue(COLORS.borderLight, COLORS.grayDark);
  const labelColor = useColorModeValue(COLORS.textLight, '#FFFFFF');
  const hoverInputLabelBg = useColorModeValue(COLORS.hoverLabelBgLight, COLORS.hoverLabelBgDark);
  const hoverInputLabelColor = useColorModeValue(COLORS.hoverLabelColorLight, COLORS.hoverLabelColorDark);
  const hoverConsumerLabelBg = useColorModeValue(COLORS.hoverConsumerLabelBgLight, COLORS.hoverConsumerLabelBgDark);
  const hoverConsumerLabelColor = useColorModeValue(COLORS.hoverConsumerLabelColorLight, COLORS.hoverConsumerLabelColorDark);
  const hoverLabelBg = isFunctional ? labelBg : isConsumer ? hoverConsumerLabelBg : hoverInputLabelBg;
  const hoverLabelColor = isFunctional ? labelColor : isConsumer ? hoverConsumerLabelColor : hoverInputLabelColor;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const onMouseEnter = () => edgeData?.onHover?.(id, true);
  const onMouseLeave = () => edgeData?.onHover?.(id, false);
  const onClick = (event: MouseEvent) => edgeData?.onDeselect?.(event);

  return (
    <>
      <g style={{ opacity: isDimmed ? DIM_OPACITY : 1, transition: 'opacity 0.2s' }}>
        <BaseEdge
          id={id}
          path={edgePath}
          style={{
            stroke: edgeColor,
            strokeWidth,
            strokeOpacity: isHovered ? 1 : 0.65,
            transition: 'stroke 0.15s, stroke-opacity 0.15s',
          }}
        />
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={16}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          // React Flow's `.inactive` class on non-clickable edges sets pointer-events:none, swallowing hover.
          style={{ cursor: 'default', pointerEvents: 'all' }}
        />
      </g>
      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              // Above the edge lines but below the cards, so the expand pills stay clickable.
              zIndex: 0,
              fontSize: 11,
              color: isHovered ? hoverLabelColor : labelColor,
              background: isHovered ? hoverLabelBg : labelBg,
              padding: '2px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              cursor: 'default',
              opacity: isDimmed ? DIM_OPACITY : 1,
              transition: 'background 0.15s, color 0.15s, opacity 0.2s',
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default SupplyChainEdge;
